import React, { useState } from 'react';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-brown text-cream/60 py-10 text-center text-sm font-body" data-id="element-32">
        <div className="max-w-4xl mx-auto px-4" data-id="element-33">
          <div className="flex items-center justify-center gap-6 mb-4" data-id="element-34">
            <button onClick={() => setActiveModal('privacy')} className="text-cream/50 hover:text-gold transition-colors duration-200 underline underline-offset-2 decoration-cream/20 hover:decoration-gold" data-id="element-35">
              Privacy Policy
            </button>
            <span className="text-cream/20" data-id="element-36">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-cream/50 hover:text-gold transition-colors duration-200 underline underline-offset-2 decoration-cream/20 hover:decoration-gold" data-id="element-37">
              Terms of Service
            </button>
          </div>
          <p data-id="element-38">
            © {new Date().getFullYear()} The Woman 90 Protocol. All rights
            reserved.
          </p>
          <p className="mt-2 text-xs text-cream/40" data-id="element-39">
            Disclaimer: The information provided is for educational purposes and
            is not intended as medical advice.
          </p>
        </div>
      </footer>

      {/* Modal Overlay */}
      {activeModal && <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-brown/60 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-40">
          <div className="bg-cream rounded-2xl shadow-warm-lg max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-41">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-8 py-5 border-b border-brown/10" data-id="element-42">
              <h2 className="font-heading text-2xl font-bold text-brown" data-id="element-43">
                {activeModal === 'privacy' ? 'Privacy Policy' : 'Terms of Service'}
              </h2>
              <button onClick={() => setActiveModal(null)} className="text-brown/40 hover:text-brown transition-colors p-1 rounded-full hover:bg-brown/5" aria-label="Close" data-id="element-44">
                <XIcon className="w-6 h-6" data-id="element-45" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-8 py-6 overflow-y-auto text-left font-body text-brown-light leading-relaxed space-y-5 text-sm" data-id="element-46">
              {activeModal === 'privacy' ? <PrivacyContent data-id="element-47" /> : <TermsContent data-id="element-48" />}
            </div>

            {/* Modal Footer */}
            <div className="px-8 py-4 border-t border-brown/10 text-center" data-id="element-49">
              <button onClick={() => setActiveModal(null)} className="bg-sage text-white font-heading font-bold px-8 py-2.5 rounded-full hover:bg-sage-light transition-colors duration-200 text-sm" data-id="element-50">
                Close
              </button>
            </div>
          </div>
        </div>}
    </>;
}
function PrivacyContent() {
  return <>
      <p className="text-xs text-brown/50 italic" data-id="element-51">
        Last updated:{' '}
        {new Date().toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      })}
      </p>

      <p data-id="element-52">
        Your privacy is important to us. This Privacy Policy explains how The
        Woman 90 Protocol ("we," "us," or "our") collects, uses, and protects
        your personal information when you visit our website or purchase our
        products.
      </p>

      <h3 className="font-heading font-bold text-lg text-brown pt-2" data-id="element-53">
        Information We Collect
      </h3>
      <p data-id="element-54">We may collect the following types of information:</p>
      <ul className="list-disc pl-5 space-y-1" data-id="element-55">
        <li data-id="element-56">
          <strong data-id="element-57">Personal Information:</strong> Name, email address, and
          billing information provided during purchase.
        </li>
        <li data-id="element-58">
          <strong data-id="element-59">Usage Data:</strong> Information about how you interact with
          our website, including pages visited and time spent.
        </li>
        <li data-id="element-60">
          <strong data-id="element-61">Cookies:</strong> Small data files stored on your device to
          improve your browsing experience.
        </li>
      </ul>

      <h3 className="font-heading font-bold text-lg text-brown pt-2" data-id="element-62">
        How We Use Your Information
      </h3>
      <ul className="list-disc pl-5 space-y-1" data-id="element-63">
        <li data-id="element-64">To process and deliver your purchase</li>
        <li data-id="element-65">To send transactional emails related to your order</li>
        <li data-id="element-66">To improve our website and products</li>
        <li data-id="element-67">To send promotional communications (only with your consent)</li>
      </ul>

      <h3 className="font-heading font-bold text-lg text-brown pt-2" data-id="element-68">
        Data Protection
      </h3>
      <p data-id="element-69">
        We implement industry-standard security measures to protect your
        personal data. We do not sell, trade, or rent your personal information
        to third parties.
      </p>

      <h3 className="font-heading font-bold text-lg text-brown pt-2" data-id="element-70">
        Your Rights
      </h3>
      <p data-id="element-71">
        You have the right to access, correct, or delete your personal data at
        any time.
      </p>
    </>;
}
function TermsContent() {
  return <>
      <p className="text-xs text-brown/50 italic" data-id="element-72">
        Last updated:{' '}
        {new Date().toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      })}
      </p>

      <p data-id="element-73">
        By purchasing or using The Woman 90 Protocol ("the Product"), you agree
        to the following Terms of Service. Please read them carefully.
      </p>

      <h3 className="font-heading font-bold text-lg text-brown pt-2" data-id="element-74">
        Digital Product Delivery
      </h3>
      <p data-id="element-75">
        The Woman 90 Protocol is a digital product. Upon successful payment, you
        will receive immediate access to all included ebooks and guides via
        email and/or a download page. No physical products will be shipped.
      </p>

      <h3 className="font-heading font-bold text-lg text-brown pt-2" data-id="element-76">
        Refund Policy
      </h3>
      <p data-id="element-77">
        We offer a 7-day "Radiant Mother" money-back guarantee. If you are not
        satisfied with the Product within 7 days of purchase, you may request a
        full refund. No questions asked.
      </p>

      <h3 className="font-heading font-bold text-lg text-brown pt-2" data-id="element-78">
        Intellectual Property
      </h3>
      <p data-id="element-79">
        All content within The Woman 90 Protocol is protected by copyright. You
        may not reproduce, distribute, or resell any part of the Product without
        written permission.
      </p>

      <h3 className="font-heading font-bold text-lg text-brown pt-2" data-id="element-80">
        Disclaimer
      </h3>
      <p data-id="element-81">
        The information provided in The Woman 90 Protocol is for educational and
        informational purposes only. It is not intended as a substitute for
        professional medical advice, diagnosis, or treatment. Always consult a
        qualified healthcare provider with any questions regarding a medical
        condition.
      </p>

      <h3 className="font-heading font-bold text-lg text-brown pt-2" data-id="element-82">
        Limitation of Liability
      </h3>
      <p data-id="element-83">
        To the fullest extent permitted by law, The Woman 90 Protocol and its
        creators shall not be liable for any indirect, incidental, or
        consequential damages arising from the use of the Product.
      </p>
    </>;
}